import { AbstractService } from '@/services/core/serviceFactory';

interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  type: string;
  healthCareCenterId: number | null;
  theme: string;
  isActive: boolean;
  entityId: number | null;
  fullName: string;
  isStaff: boolean;
  newCC: number | null;
  showActivityDuration: number | null;
  ccWeeklyHr: number | null;
}

interface ProfileResponse {
  data: {
    isAppointmentEnabled: boolean;
    profile: UserProfile;
    adminClinicType: number;
    clinicOpeningTimeUtc: string | null;
    PULSE_MIN_THRESHOLD: string;
    isTeleHealthEnabled: boolean;
    clinicTimezone: string | null;
    clinicClosingTimeUtc: string | null;
    PULSE_MAX_THRESHOLD: string;
  };
}

interface GlobalTimelogStartResponse {
  timeLogId: number;
  success: boolean;
  message: string;
}

export class GlobalTimelogService extends AbstractService {
  /**
   * Get user profile by email
   */
  async getUserProfile(email: string): Promise<ProfileResponse> {
    this.requireAuth();

    if (!email) {
      throw new Error('Email is required');
    }

    try {
      const response = await this.apiClient.get(`/api/user/${email}/profile`, this.context.token);

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Failed to get user profile');
      }
    } catch (error) {
      throw new Error('Failed to get user profile');
    }
  }

  /**
   * Start a new global timelog session
   */
  async startGlobalTimelog(userId: number): Promise<GlobalTimelogStartResponse> {
    this.requireAuth();

    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const response = await this.apiClient.post(`/api/pep/${userId}/timelog`, {
        duration: 0,
        status: "inprogress"
      }, this.context.token);

      if (response.data && response.data.success && response.data.timeLogId) {
        return {
          timeLogId: response.data.timeLogId,
          success: true,
          message: response.data.message || 'Global timelog started successfully'
        };
      } else {
        return {
          timeLogId: 0,
          success: false,
          message: response.data?.message || 'Failed to start global timelog'
        };
      }
    } catch (error) {
      throw new Error('Failed to start global timelog');
    }
  }

  /**
   * Update an existing global timelog session
   */
  async updateGlobalTimelog(userId: number, timeLogId: number, duration: number): Promise<boolean> {
    this.requireAuth();

    if (!userId || !timeLogId) {
      throw new Error('User ID and TimeLog ID are required');
    }

    try {
      const response = await this.apiClient.post(`/api/pep/${userId}/timelog/${timeLogId}`, {
        duration,
        status: "inprogress"
      }, this.context.token);

      return response.data?.success || true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Complete a global timelog session
   */
  async completeGlobalTimelog(userId: number, timeLogId: number, duration: number): Promise<boolean> {
    this.requireAuth();

    if (!userId || !timeLogId) {
      throw new Error('User ID and TimeLog ID are required');
    }

    try {
      const response = await this.apiClient.post(`/api/pep/${userId}/timelog/${timeLogId}`, {
        duration,
        status: "completed"
      }, this.context.token);

      return response.data?.success || true;
    } catch (error) {
      return false;
    }
  }
}